// @refresh reset
import React, { useState, useRef, useEffect } from 'react';
import {
  ChevronRight,
  Columns3,
  Eye,
  EyeOff,
  File as FileIcon,
  Layers,
  LayoutGrid,
  Lock,
  Minus,
  Tag,
  Ticket,
} from 'lucide-react';
import { useRouteMatch } from 'react-router-dom';
import clsx from 'clsx';
import { confirm } from '@dashboard/lib/confirm';
import ColorSelectDual from '@dashboard/components/Formik/components/ColorSelectDual';
import FontCombobox from '@dashboard/components/Formik/components/FontCombobox/FontCombobox';
import { FontWeightSelectInner } from '@dashboard/components/Formik/components/FontWeightSelect';
import LabelToggle from '@/dashboard/components/Formik/components/LabelToggle';
import Heading from '@dashboard/components/Formik/components/Heading';
import { InfoTooltip } from '@dashboard/components/Tooltip';
import { DESIGN_SECTION_ICONS } from './designSectionIcons';
import Columns from '@dashboard/components/Formik/components/Columns';
import Collapsible from '@/dashboard/components/Collapsible';
import PricePickerPresets from './PricePickerPresets';
import OrderBumpPresets from './OrderBumpPresets';

import Label from '@dashboard/components/Form/components/Label';
import { Field, getFieldComponentByType } from '@dashboard/components/Formik/Form';
import { FormExtensionContext, getNestedValue } from '@dashboard/components/Formik/formUtils';
import { Badge } from '@/dashboard/components/ui/badge';
import Link from '@dashboard/components/Link';
import CanUseFeature from '@dashboard/components/CanUseFeature';
import Features from '@dashboard/enums/features';

// Muted help text under a field group. Optionally renders an inline link to
// another tab of the current page (field.link = { label, tab }) — e.g. "You can
// change the tabs text in the Product tab". The page id is read from the route
// so the caption stays a static registry node.
function CaptionContent({ field }) {
  const match = useRouteMatch('/pages/:id');
  const pageId = match?.params?.id;
  return (
    <div className="text-xs text-gray-500 dark:text-muted-foreground leading-relaxed">
      {field.text}
      {field.link && pageId && (
        <Link to={`/pages/${pageId}/customize/${field.link.tab}`} color="default">
          {field.link.label}
        </Link>
      )}
      {field.textAfter}
    </div>
  );
}

function ResetIcon(props) {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M3 12a9 9 0 1 0 3-6.7" />
      <path d="M3 4v5h5" />
    </svg>
  );
}

function pickHexFromColorField(colorField, values) {
  if (!colorField) return '';
  const value = getNestedValue(values, colorField.name);
  const mode = colorField.currentColorMode || 'light';
  const fromValue = value?.[mode];
  if (fromValue) return fromValue;
  const fromDefault = colorField.defaultColors?.[mode];
  return fromDefault || '';
}

function SubHeader({ label, divider }) {
  // -mb-1 keeps the subheader close to its first content row.
  // mt-3 (with first:mt-0) adds space above non-first subheaders so distinct
  // sub-groups (e.g. Layout / Elements) read as separate.
  return (
    <div
      className={clsx(
        'text-sm font-semibold text-gray-900 dark:text-foreground',
        divider ? 'border-t border-gray-100 dark:border-border pt-4 mt-4' : 'mt-3 first:mt-0',
      )}
    >
      {label}
    </div>
  );
}

function EyebrowGroup({ title, children, className }) {
  return (
    <div className={className}>
      <div className="text-xs font-semibold uppercase tracking-[0.7px] text-gray-500 dark:text-muted-foreground mb-1">
        {title}
      </div>
      <div className="flex flex-col gap-2">{children}</div>
    </div>
  );
}

// Walks a flat field list and groups each `subgroup` field with the non-subgroup
// fields that follow it, so the renderer can wrap each section in its own
// container. A `subheader` ends any open group (it starts a new top-level
// section like Typography). Yields a flat list of either `{ kind: 'field' }` or
// `{ kind: 'group', title, fields }` items.
function chunkFieldsBySubgroup(fields) {
  const out = [];
  let open = null;
  for (const field of fields) {
    if (!field) continue;
    if (field.type === 'subgroup') {
      open = { kind: 'group', title: field.label, fields: [] };
      out.push(open);
      continue;
    }
    if (field.type === 'subheader') {
      open = null;
      out.push({ kind: 'field', field });
      continue;
    }
    if (open) {
      open.fields.push(field);
    } else {
      out.push({ kind: 'field', field });
    }
  }
  return out;
}

const ICON_BTN_CLASS =
  'h-[22px] w-[22px] rounded-[5px] flex items-center justify-center border border-transparent hover:bg-gray-50 dark:hover:bg-muted hover:border-gray-200 dark:hover:border-border';

// Matches the height of a color-swatch row that shows a hex line below the
// label, so rows without a swatch (Divider, Page, Column, etc.) align.
const PART_ROW_MIN_H = 'min-h-[52px]';

// Picks an icon for an element row that doesn't have a primary color swatch.
// Match against suffix patterns of field.id so split/double/center variants
// resolve to the same icon (e.g. split-layout-page → FileIcon).
function getElementIcon(fieldId) {
  if (!fieldId) return LayoutGrid;
  const id = fieldId;
  if (id.endsWith('-divider')) return Minus;
  if (id.endsWith('-page')) return FileIcon;
  if (id.endsWith('-column')) return Columns3;
  if (id.endsWith('-card')) return Layers;
  if (id.endsWith('-ticket')) return Ticket;
  if (id.endsWith('-variant')) return LayoutGrid;
  if (id.startsWith('badges-')) return Tag;
  return LayoutGrid;
}

// Renders at the exact footprint of the color swatch (`h-4 w-4 rounded`) so
// every element row shares the same left-side inset rhythm.
function ElementIconTile({ fieldId }) {
  const Icon = getElementIcon(fieldId);
  return (
    <div className="h-4 w-4 rounded bg-gray-100 dark:bg-muted dark:ring-1 dark:ring-foreground/25 flex items-center justify-center shrink-0 text-gray-400 dark:text-muted-foreground">
      <Icon className="h-3 w-3" strokeWidth={2} />
    </div>
  );
}

function ResetButton({ isAtDefault, resetTier, onClick, className = '' }) {
  return (
    <button
      type="button"
      disabled={isAtDefault}
      onClick={onClick}
      className={`${ICON_BTN_CLASS} ${className} ${isAtDefault ? 'text-gray-300 cursor-default hover:bg-transparent hover:border-transparent' : 'text-gray-500'}`}
      title={
        isAtDefault ? 'Already at default' : resetTier === 2 ? 'Clear to default' : 'Reset to theme'
      }
    >
      <ResetIcon className="transition-colors" />
    </button>
  );
}

function ToggleButton({ enabled, label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`${ICON_BTN_CLASS} text-gray-500 dark:text-muted-foreground`}
      title={enabled ? `Hide ${label}` : `Show ${label}`}
    >
      {enabled ? (
        <Eye className="h-3 w-3 transition-colors" />
      ) : (
        <EyeOff className="h-3 w-3 transition-colors" />
      )}
    </button>
  );
}

async function handleTieredReset({ resetTier, reset, resetToDefaults }) {
  if (resetTier === 2) {
    const confirmed = await confirm('Remove theme value?', {
      description:
        'This will clear the value from your theme so it uses the system default instead.',
      confirm: 'Clear value',
      cancel: 'Cancel',
    });
    if (confirmed) (resetToDefaults || reset)();
  } else {
    reset();
  }
}

const expandAll =
  typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('expand');

function CollapsibleFieldGroup({ field, values, setFieldValue, inputClassName, errors, touched }) {
  const [isOpen, setIsOpen] = useState(expandAll || field.defaultOpen);
  const prevDefaultOpen = useRef(field.defaultOpen);

  // Auto-open when defaultOpen changes to true (e.g., user switches layout or logo style).
  // Auto-close only when the section's shouldBeOpen explicitly returns false
  // (e.g., switching between mutually exclusive layout/logo sub-sections).
  useEffect(() => {
    if (expandAll) return;
    if (field.defaultOpen && !prevDefaultOpen.current) {
      setIsOpen(true);
    } else if (!field.defaultOpen && prevDefaultOpen.current && field.autoClose) {
      setIsOpen(false);
    }
    prevDefaultOpen.current = field.defaultOpen;
  }, [field.defaultOpen, field.autoClose]);

  // Expand when navigated to via nav click or preview click
  useEffect(() => {
    const sectionId = field.id;
    if (!sectionId) return;
    const handler = e => {
      if (e.detail === sectionId) setIsOpen(true);
    };
    window.addEventListener('expand-design-section', handler);
    return () => window.removeEventListener('expand-design-section', handler);
  }, [field.id]);

  // The first field is always the heading — use it as the trigger
  const headingField = field.fields[0];
  const contentFields = field.fields.slice(1);

  const cardStyle = field.cardStyle;

  return (
    <div
      id={field.id}
      className={clsx(
        'scroll-mt-20',
        {
          '[&+&]:mt-2': !cardStyle,
          'border-b border-gray-100 dark:border-border pb-4': field.divider && !cardStyle,
          'border border-gray-200 dark:border-border rounded-lg bg-white dark:bg-background [&+&]:mt-2':
            cardStyle,
        },
        field.className,
      )}
    >
      <Collapsible.Root open={isOpen} onOpenChange={setIsOpen}>
        <div
          className={clsx('flex items-center gap-x-2 w-full', {
            'px-3 py-2.5': cardStyle,
          })}
        >
          <div className="flex-1 text-left">
            <RenderHeadingField
              field={headingField}
              isRelevant={field.isRelevant !== false}
              isOpen={isOpen}
              onToggle={() => setIsOpen(prev => !prev)}
            />
          </div>
        </div>

        <Collapsible.Content className="CollapsibleContentAnimation">
          <div
            className={clsx('flex flex-col gap-y-2', {
              'mt-4': !cardStyle,
              'px-3 pb-3': cardStyle,
              'opacity-50 pointer-events-none':
                headingField.canToggle && headingField.enabled === false,
            })}
          >
            {chunkFieldsBySubgroup(contentFields).map((item, index) => {
              if (item.kind === 'group') {
                return (
                  <EyebrowGroup
                    key={`group-${item.title}-${index}`}
                    title={item.title}
                    className="[&+&]:mt-1"
                  >
                    {item.fields.map((child, i) => (
                      <InnerField
                        key={child.name || child.id || `field-${i}`}
                        field={child}
                        values={values}
                        setFieldValue={setFieldValue}
                        inputClassName={inputClassName}
                        errors={errors}
                        touched={touched}
                      />
                    ))}
                  </EyebrowGroup>
                );
              }
              const child = item.field;
              return (
                <InnerField
                  key={child.name || child.id || `field-${index}`}
                  field={child}
                  values={values}
                  setFieldValue={setFieldValue}
                  inputClassName={inputClassName}
                  errors={errors}
                  touched={touched}
                />
              );
            })}
          </div>
        </Collapsible.Content>
      </Collapsible.Root>
    </div>
  );
}

function RenderHeadingField({ field, isRelevant, isOpen, onToggle }) {
  const {
    canReset = false,
    canToggle = false,
    reset,
    resetToDefaults,
    resetTier,
    isAtDefault,
    toggle,
    tooltip,
    description,

    ...rest
  } = field;

  return (
    <div className="relative">
      <div className="flex items-center gap-x-2 relative">
        <button type="button" className="cursor-pointer" onClick={onToggle}>
          <Heading {...rest} icon={DESIGN_SECTION_ICONS[rest.id]} />
        </button>
        {tooltip && <InfoTooltip className="-ml-2">{tooltip}</InfoTooltip>}

        <div className="flex items-center gap-1 absolute top-0 right-0 z-0">
          <div className="flex items-center gap-1">
            {!isRelevant && (
              <Badge variant="outline" className="cursor-default">
                Not on page
              </Badge>
            )}

            {canToggle && (
              <ToggleButton enabled={field.enabled} label={field.label} onClick={toggle} />
            )}

            {canReset && (
              <ResetButton
                isAtDefault={isAtDefault}
                resetTier={resetTier}
                onClick={() => handleTieredReset({ resetTier, reset, resetToDefaults })}
                className="ml-auto"
              />
            )}
          </div>

          <button
            type="button"
            className={`${ICON_BTN_CLASS} text-gray-500 dark:text-muted-foreground shrink-0 cursor-pointer`}
            onClick={onToggle}
          >
            <ChevronRight
              className={clsx('h-3 w-3 transition-transform duration-200', {
                'rotate-90': isOpen,
              })}
            />
          </button>
        </div>
      </div>
      {description && (
        <div className="text-xs text-gray-500 dark:text-muted-foreground mt-0.5">{description}</div>
      )}
    </div>
  );
}

function InnerField({ field, values, setFieldValue, inputClassName, errors, touched }) {
  const {
    customFieldPropsAreEqual,
    customLeafFieldNames,
    customContainerChildrenPropsEqual,
    customContainerValuesEqual,
  } = React.useContext(FormExtensionContext);
  const rendered = renderDesignField(field, {
    values,
    setFieldValue,
    inputClassName,
    errors,
    touched,
  });
  if (rendered) return rendered;

  // conditional-wrapper isn't in getFieldComponentByType — handle it explicitly.
  // Render children through InnerField (not Form.js's Field, which would wrap
  // each in a FormElement with mb-4) so spacing matches CollapsibleFieldGroup's
  // gap-y-2 rhythm used by every other design section.
  if (field.type === 'conditional-wrapper') {
    const children = Array.isArray(field.children) ? field.children : [field.children];
    return (
      <div
        className={clsx('flex flex-col gap-y-2', field.className, {
          'pointer-events-none [&_input]:opacity-50 [&_select]:opacity-50 [&_label]:opacity-50 [&_img]:opacity-50 [&_.rounded-full]:opacity-50 [&_h3]:opacity-50 [&_h4]:opacity-50 [&_h5]:opacity-50 [&_span]:opacity-50 [&_p]:opacity-50':
            field.enabled === false,
        })}
      >
        {children.filter(Boolean).map((child, index) => (
          <InnerField
            key={child?.name || child?.id || `cw-${index}`}
            field={child}
            values={values}
            setFieldValue={setFieldValue}
            inputClassName={inputClassName}
            errors={errors}
            touched={touched}
          />
        ))}
      </div>
    );
  }

  // Render Form.js's Field with hideLabel:true (preserves Formik wiring + value
  // population), and prepend our own compact 11px label to match the design's
  // eyebrow-field label style.
  const showCompactLabel = field.label && !field.hideLabel;
  return (
    <div className={field.wrapperClassName}>
      {showCompactLabel && (
        <div className="text-xs font-medium text-gray-700 dark:text-muted-foreground mb-1.5">
          {field.label}
        </div>
      )}
      <Field
        field={{ ...field, noMargin: true, hideLabel: true }}
        values={values}
        setFieldValue={setFieldValue}
        inputClassName={inputClassName}
        errors={errors}
        touched={touched}
        customFieldPropsAreEqual={customFieldPropsAreEqual}
        customLeafFieldNames={customLeafFieldNames}
        customContainerChildrenPropsEqual={customContainerChildrenPropsEqual}
        customContainerValuesEqual={customContainerValuesEqual}
      />
    </div>
  );
}

function ColorGroupWithFonts({ field, values, setFieldValue, inputClassName, errors, touched }) {
  const [isOpen, setIsOpen] = useState(field.fontGroupHasChanges);

  return (
    <Collapsible.Root open={isOpen} onOpenChange={setIsOpen}>
      <div className="flex items-center gap-x-2 relative">
        <div
          className={`max-w-2xs flex-1 flex items-center gap-x-2 ${field.groupEnabled === false ? 'opacity-50 pointer-events-none' : ''}`}
        >
          <Label className="truncate !mb-0">{field.label}</Label>

          <div className="">
            <ColorSelectDual
              name={field.name}
              value={getNestedValue(values, field.name)}
              onChange={v => setFieldValue(field.name, v)}
              picker={field.picker}
              currentColorMode={field.currentColorMode}
              defaultColors={field.defaultColors}
              swatchClassName="h-4 w-4 rounded"
            />
          </div>
        </div>

        {field.groupCanToggle || field.groupCanReset ? (
          <div className="flex items-center gap-1 absolute right-0 top-0 pointer-events-auto">
            {field.groupCanToggle && (
              <ToggleButton
                enabled={field.groupEnabled}
                label={field.groupLabel || field.label}
                onClick={field.groupToggle}
              />
            )}

            {field.groupCanReset && (
              <ResetButton
                isAtDefault={field.groupIsAtDefault}
                resetTier={field.groupResetTier}
                onClick={() =>
                  handleTieredReset({
                    resetTier: field.groupResetTier,
                    reset: field.groupReset,
                    resetToDefaults: field.groupResetToDefaults,
                  })
                }
                className="ml-auto"
              />
            )}

            <Collapsible.Trigger asChild>
              <button
                type="button"
                className={`${ICON_BTN_CLASS} text-gray-500 dark:text-muted-foreground`}
                title={isOpen ? 'Hide font settings' : 'Show font settings'}
              >
                <ChevronRight
                  className={clsx('h-3 w-3 transition-transform duration-200', {
                    'rotate-90': isOpen,
                  })}
                />
              </button>
            </Collapsible.Trigger>
          </div>
        ) : (
          <div className="flex items-center gap-1 absolute right-0 top-0 pointer-events-auto">
            {field.canReset && (
              <ResetButton
                isAtDefault={field.isAtDefault}
                resetTier={field.resetTier}
                onClick={() =>
                  handleTieredReset({
                    resetTier: field.resetTier,
                    reset: field.reset,
                  })
                }
              />
            )}

            <Collapsible.Trigger asChild>
              <button
                type="button"
                className={`${ICON_BTN_CLASS} text-gray-500 dark:text-muted-foreground`}
                title={isOpen ? 'Hide font settings' : 'Show font settings'}
              >
                <ChevronRight
                  className={clsx('h-3 w-3 transition-transform duration-200', {
                    'rotate-90': isOpen,
                  })}
                />
              </button>
            </Collapsible.Trigger>
          </div>
        )}
      </div>

      <Collapsible.Content className="CollapsibleContentAnimation">
        <div
          className={clsx('mt-3', {
            'pointer-events-none [&_input]:opacity-50 [&_select]:opacity-50 [&_label]:opacity-50':
              field.fontGroupEnabled === false,
          })}
        >
          <Columns
            columns={field.fontGroupColumns}
            fields={field.fontGroupFields}
            gap="sm"
            isDesignForm={true}
            values={values}
            setFieldValue={setFieldValue}
            inputClassName={inputClassName}
            errors={errors}
            touched={touched}
          />
        </div>
      </Collapsible.Content>
    </Collapsible.Root>
  );
}

// PartColor: color control nested inside an eyebrow. Label above; a 30px-tall
// box below with swatch + hex + reset. Mirrors the design's <PartColor>.
function PartColor({ field, values, setFieldValue }) {
  const hex = pickHexFromColorField(field, values);
  return (
    <div>
      <div className="text-xs font-medium text-gray-700 dark:text-muted-foreground mb-1.5">
        {field.label}
      </div>
      <div className="border border-gray-200 dark:border-border rounded-md bg-white dark:bg-background flex items-center gap-2 h-[30px] pl-2 pr-1">
        <ColorSelectDual
          name={field.name}
          value={getNestedValue(values, field.name)}
          onChange={v => setFieldValue(field.name, v)}
          picker={field.picker}
          currentColorMode={field.currentColorMode}
          defaultColors={field.defaultColors}
          swatchClassName="h-3.5 w-3.5 rounded-sm"
        />
        <span className="flex-1 text-xs text-gray-500 dark:text-muted-foreground font-mono uppercase truncate">
          {hex}
        </span>
        {field.canToggle && (
          <ToggleButton enabled={field.enabled} label={field.label} onClick={field.toggle} />
        )}
        {field.canReset && (
          <ResetButton
            isAtDefault={field.isAtDefault}
            resetTier={field.resetTier}
            onClick={() =>
              handleTieredReset({
                resetTier: field.resetTier,
                reset: field.reset,
                resetToDefaults: field.resetToDefaults,
              })
            }
          />
        )}
      </div>
    </div>
  );
}

// Inside the badge row's expand: label above, swatch+hex inline at 30px.
// No per-cell reset — reset stays on the row header.
function BadgeColorCell({ label, colorField, values, setFieldValue }) {
  const hex = pickHexFromColorField(colorField, values);
  return (
    <div>
      <div className="text-xs font-medium text-gray-700 dark:text-muted-foreground mb-1.5">
        {label}
      </div>
      <div className="border border-gray-200 dark:border-border rounded-md bg-white dark:bg-background flex items-center gap-2 h-[30px] px-2">
        <ColorSelectDual
          name={colorField.name}
          value={getNestedValue(values, colorField.name)}
          onChange={v => setFieldValue(colorField.name, v)}
          picker={colorField.picker}
          currentColorMode={colorField.currentColorMode}
          defaultColors={colorField.defaultColors}
          swatchClassName="h-3.5 w-3.5 rounded-sm"
        />
        <span className="flex-1 text-xs text-gray-500 dark:text-muted-foreground font-mono uppercase truncate">
          {hex}
        </span>
      </div>
    </div>
  );
}

function PartRow({ field, values, setFieldValue, inputClassName, errors, touched }) {
  const { variant, primaryColorField, eyebrows, showEyebrows } = field;
  // Only show the expand chevron when there's actually expandable content. A pure
  // toggle row (e.g. the branding badges — no style fields) has nothing to expand.
  const hasExpandableContent = (eyebrows || []).some(eb => (eb.fields || []).length > 0);
  const hasExpand =
    variant !== 'color-only' &&
    variant !== 'toggle-only' &&
    variant !== 'badge' &&
    hasExpandableContent;
  const [isOpen, setIsOpen] = useState(false);

  const hex = primaryColorField ? pickHexFromColorField(primaryColorField, values) : '';

  const renderRowControls = () => {
    // Locked branding badges: one quiet lock, no eye/reset/chevron. The whole row is
    // the upgrade-popover trigger, so those controls would only add clutter and read
    // as interactive when they aren't.
    if (field.locked) {
      return (
        <div className="flex items-center">
          <span className={`${ICON_BTN_CLASS} text-gray-400 dark:text-muted-foreground`}>
            <Lock className="h-3.5 w-3.5" />
          </span>
        </div>
      );
    }

    return (
      <div className="flex items-center gap-1">
        {field.canToggle && field.showEye && (
          <ToggleButton enabled={field.enabled} label={field.label} onClick={field.toggle} />
        )}
        {variant !== 'toggle-only' && field.canReset && (
          <ResetButton
            isAtDefault={field.isAtDefault}
            resetTier={field.resetTier}
            onClick={() =>
              handleTieredReset({
                resetTier: field.resetTier,
                reset: field.reset,
                resetToDefaults: field.resetToDefaults,
              })
            }
          />
        )}
        {hasExpand && (
          <button
            type="button"
            className={`${ICON_BTN_CLASS} text-gray-500 dark:text-muted-foreground`}
            onClick={() => setIsOpen(o => !o)}
            title={isOpen ? 'Collapse' : 'Expand'}
          >
            <ChevronRight
              className={clsx('h-3 w-3 transition-transform duration-200', {
                'rotate-90': isOpen,
              })}
            />
          </button>
        )}
      </div>
    );
  };

  // Render eyebrow contents (used by all expandable variants)
  const renderEyebrowContent = () => {
    if (!isOpen) return null;
    const visible = showEyebrows ? eyebrows : eyebrows.flatMap(eb => eb.fields);

    // Group colors and non-colors separately, then pair within each group into
    // 2-col rows. An orphan keeps to one column rather than stretching: a lone
    // Radius under [Style | Width] otherwise reads as the most important field
    // in the group just for being last.
    // A font group is a 2×2 grid of its own, so it takes a whole row instead
    // of one cell of one.
    const isFullWidth = f => f.type === 'font-group-columns';
    const buildRows = list => {
      const rows = [];
      let pending = [];
      for (const f of list) {
        if (isFullWidth(f)) {
          if (pending.length) rows.push(pending);
          pending = [];
          rows.push([f]);
        } else {
          pending.push(f);
          if (pending.length === 2) {
            rows.push(pending);
            pending = [];
          }
        }
      }
      if (pending.length) rows.push(pending);
      return rows;
    };
    const renderPaired = (fields, orderMode = 'colorsFirst') => {
      let allRows;
      if (orderMode === 'declared') {
        // Preserve the declared field order and pair consecutively, mixing
        // colors and controls in a row (e.g. Marker: Shape | Color at 50/50).
        allRows = buildRows(fields);
      } else {
        const colors = fields.filter(f => f.type === 'color-select-dual');
        const others = fields.filter(f => f.type !== 'color-select-dual');
        allRows = [...buildRows(colors), ...buildRows(others)];
      }
      return (
        <div className="flex flex-col gap-2">
          {allRows.map((row, ri) => (
            <div key={ri} className="grid grid-cols-2 gap-2">
              {row.map((f, i) =>
                f.type === 'color-select-dual' ? (
                  <PartColor
                    key={f.name || f.id || i}
                    field={f}
                    values={values}
                    setFieldValue={setFieldValue}
                  />
                ) : (
                  <div key={f.name || f.id || i} className={clsx({ 'col-span-2': isFullWidth(f) })}>
                    <InnerField
                      field={f}
                      values={values}
                      setFieldValue={setFieldValue}
                      inputClassName={inputClassName}
                      errors={errors}
                      touched={touched}
                    />
                  </div>
                ),
              )}
            </div>
          ))}
        </div>
      );
    };

    return (
      <div className="px-3 pb-3 pt-2 flex flex-col gap-4">
        {showEyebrows
          ? eyebrows.map(eb => (
              <EyebrowGroup key={eb.id} title={eb.title}>
                {renderPaired(eb.fields, eb.id === 'marker' ? 'declared' : 'colorsFirst')}
              </EyebrowGroup>
            ))
          : renderPaired(visible)}
      </div>
    );
  };

  // Color-only: bare row with swatch (popover trigger via ColorSelectDual) + label + hex + reset
  if (variant === 'color-only' && primaryColorField) {
    return (
      <div className="border border-gray-200 dark:border-border rounded-lg bg-white dark:bg-background">
        <div className={`flex items-center gap-2.5 px-2.5 py-2 ${PART_ROW_MIN_H}`}>
          <ColorSelectDual
            name={primaryColorField.name}
            value={getNestedValue(values, primaryColorField.name)}
            onChange={v => setFieldValue(primaryColorField.name, v)}
            picker={primaryColorField.picker}
            currentColorMode={primaryColorField.currentColorMode}
            defaultColors={primaryColorField.defaultColors}
            swatchClassName="h-4 w-4 rounded"
          />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-gray-900 dark:text-foreground truncate">
              {field.label}
            </div>
            {hex && (
              <div className="text-[10.5px] text-gray-400 dark:text-muted-foreground font-mono tracking-wide uppercase">
                {hex}
              </div>
            )}
          </div>
          {renderRowControls()}
        </div>
      </div>
    );
  }

  // Toggle-only: row with label + toggle on the right
  if (variant === 'toggle-only') {
    const toggleField = field.builtFields?.[field.paths?.[0]];
    return (
      <div className="border border-gray-200 dark:border-border rounded-lg bg-white dark:bg-background">
        <div className={`flex items-center gap-2.5 px-2.5 py-2 ${PART_ROW_MIN_H}`}>
          <ElementIconTile fieldId={field.id} />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-gray-900 dark:text-foreground truncate">
              {field.label}
            </div>
          </div>
          {toggleField && (
            <InnerField
              field={toggleField}
              values={values}
              setFieldValue={setFieldValue}
              inputClassName={inputClassName}
              errors={errors}
              touched={touched}
            />
          )}
        </div>
      </div>
    );
  }

  // Badge: label-only row header with reset + expand. On expand, 2-col grid of
  // Text / Background ColorSelectDual pickers.
  if (variant === 'badge') {
    const { badgeTextField, badgeBackgroundField } = field;
    return (
      <div className="border border-gray-200 dark:border-border rounded-lg bg-white dark:bg-background">
        <div
          className={`flex items-center gap-2.5 px-2.5 py-2 cursor-pointer ${PART_ROW_MIN_H}`}
          onClick={() => setIsOpen(o => !o)}
        >
          <ElementIconTile fieldId={field.id} />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-gray-900 dark:text-foreground truncate">
              {field.label}
            </div>
          </div>
          <div onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-1">
              {field.canReset && (
                <ResetButton
                  isAtDefault={field.isAtDefault}
                  resetTier={field.resetTier}
                  onClick={() =>
                    handleTieredReset({
                      resetTier: field.resetTier,
                      reset: field.reset,
                      resetToDefaults: field.resetToDefaults,
                    })
                  }
                />
              )}
              <button
                type="button"
                className={`${ICON_BTN_CLASS} text-gray-500 dark:text-muted-foreground`}
                onClick={() => setIsOpen(o => !o)}
                title={isOpen ? 'Collapse' : 'Expand'}
              >
                <ChevronRight
                  className={clsx('h-3 w-3 transition-transform duration-200', {
                    'rotate-90': isOpen,
                  })}
                />
              </button>
            </div>
          </div>
        </div>
        {isOpen && (
          <div className="px-3 pb-3 pt-2 grid grid-cols-2 gap-2">
            {badgeTextField && (
              <BadgeColorCell
                label="Text"
                colorField={badgeTextField}
                values={values}
                setFieldValue={setFieldValue}
              />
            )}
            {badgeBackgroundField && (
              <BadgeColorCell
                label="Background"
                colorField={badgeBackgroundField}
                values={values}
                setFieldValue={setFieldValue}
              />
            )}
          </div>
        )}
      </div>
    );
  }

  // Default expandable row (text / plain)
  return (
    <div className="border border-gray-200 dark:border-border rounded-lg bg-white dark:bg-background">
      <div
        className={clsx(`flex items-center gap-2.5 px-2.5 py-2 ${PART_ROW_MIN_H}`, {
          'cursor-pointer': hasExpand,
        })}
        onClick={hasExpand ? () => setIsOpen(o => !o) : undefined}
      >
        {primaryColorField ? (
          <div onClick={e => e.stopPropagation()}>
            <ColorSelectDual
              name={primaryColorField.name}
              value={getNestedValue(values, primaryColorField.name)}
              onChange={v => setFieldValue(primaryColorField.name, v)}
              picker={primaryColorField.picker}
              currentColorMode={primaryColorField.currentColorMode}
              defaultColors={primaryColorField.defaultColors}
              swatchClassName="h-4 w-4 rounded"
            />
          </div>
        ) : (
          <ElementIconTile fieldId={field.id} />
        )}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-gray-900 dark:text-foreground truncate">
            {field.label}
          </div>
          {hex && (
            <div className="text-[10.5px] text-gray-400 dark:text-muted-foreground font-mono tracking-wide mt-0.5 uppercase">
              {hex}
            </div>
          )}
        </div>
        {/* "Not on page" badges are a SECTION-level affordance (Discount code,
            Manual payments, …) — element rows inside a section never carry
            them; rows for absent content just render plainly. */}
        <div onClick={e => e.stopPropagation()}>{renderRowControls()}</div>
      </div>
      {renderEyebrowContent()}
    </div>
  );
}

/**
 * Renders design-specific field types.
 * Returns a React element for known design types, or null to fall back to Form.js generic rendering.
 */
export function renderDesignField(
  field,
  { values, setFieldValue, inputClassName, errors, touched },
) {
  switch (field.type) {
    case 'fieldgroup': {
      if (!field.collapsible) return null;
      return (
        <CollapsibleFieldGroup
          field={field}
          values={values}
          setFieldValue={setFieldValue}
          inputClassName={inputClassName}
          errors={errors}
          touched={touched}
        />
      );
    }

    case 'subheader':
      return <SubHeader label={field.label} divider={field.divider} />;

    case 'preset-picker': {
      const Presets = field.id === 'order-bump-presets' ? OrderBumpPresets : PricePickerPresets;
      return <Presets values={values} setFieldValue={setFieldValue} />;
    }

    case 'subgroup':
      // Fallback only — CollapsibleFieldGroup wraps subgroups via EyebrowGroup
      // through chunkFieldsBySubgroup, which is the path that actually renders.
      return (
        <div className="text-xs font-bold uppercase tracking-[0.7px] text-gray-500 dark:text-muted-foreground">
          {field.label}
        </div>
      );

    case 'caption':
      // Caption hugs the field group above it (e.g. the Columns colors block in
      // Global). Negative margin compensates for the parent's gap-y-4 so it sits
      // ~6px below the preceding row, per the design. Supports an optional inline
      // link to another page tab (field.link) — see CaptionContent.
      return <CaptionContent field={field} />;

    case 'part-row': {
      const partRow = (
        <PartRow
          field={field}
          values={values}
          setFieldValue={setFieldValue}
          inputClassName={inputClassName}
          errors={errors}
          touched={touched}
        />
      );
      // Branding badges (Powered by Stripe / Checkout Page) are plan-gated: free/trial
      // sellers can't change them. The row shows its own quiet lock, so CanUseFeature
      // here only adds the click-to-upgrade popover (no second lock icon, no dimming).
      return field.locked ? (
        <CanUseFeature
          feature={Features.CHECKOUT_PAGE_BADGE}
          isDisabled
          showIcon={false}
          reduceOpacity={false}
          description={field.lockMessage}
        >
          {partRow}
        </CanUseFeature>
      ) : (
        partRow
      );
    }

    case 'color-select-dual': {
      const hex = pickHexFromColorField(field, values);
      return (
        <div
          className={clsx(
            'border border-gray-200 dark:border-border rounded-md bg-white dark:bg-background flex items-center gap-2.5 px-2.5 py-1.5 min-h-[34px]',
            { 'opacity-50 pointer-events-none': field.groupEnabled === false },
          )}
        >
          <ColorSelectDual
            name={field.name}
            value={getNestedValue(values, field.name)}
            onChange={v => setFieldValue(field.name, v)}
            picker={field.picker}
            currentColorMode={field.currentColorMode}
            defaultColors={field.defaultColors}
            swatchClassName="h-4 w-4 rounded"
          />
          <div className="flex-1 min-w-0">
            <div className="text-xs font-medium text-gray-900 dark:text-foreground truncate leading-tight">
              {field.label}
            </div>
            {hex && (
              <div className="text-[10.5px] text-gray-400 dark:text-muted-foreground font-mono tracking-wide uppercase">
                {hex}
              </div>
            )}
          </div>
          {field.groupCanToggle || field.groupCanReset ? (
            <div className="flex items-center gap-1">
              {field.groupCanToggle && (
                <ToggleButton
                  enabled={field.groupEnabled}
                  label={field.groupLabel || field.label}
                  onClick={field.groupToggle}
                />
              )}
              {field.groupCanReset && (
                <ResetButton
                  isAtDefault={field.groupIsAtDefault}
                  resetTier={field.groupResetTier}
                  onClick={() =>
                    handleTieredReset({
                      resetTier: field.groupResetTier,
                      reset: field.groupReset,
                      resetToDefaults: field.groupResetToDefaults,
                    })
                  }
                />
              )}
            </div>
          ) : (
            field.canReset && (
              <ResetButton
                isAtDefault={field.isAtDefault}
                resetTier={field.resetTier}
                onClick={() =>
                  handleTieredReset({
                    resetTier: field.resetTier,
                    reset: field.reset,
                  })
                }
              />
            )
          )}
        </div>
      );
    }

    case 'color-group-with-fonts':
      return (
        <ColorGroupWithFonts
          field={field}
          values={values}
          setFieldValue={setFieldValue}
          inputClassName={inputClassName}
          errors={errors}
          touched={touched}
        />
      );

    case 'font-group-columns':
      return (
        <Columns
          columns={field.columns}
          fields={field.fields}
          gap={field.gap}
          isDesignForm={field.isDesignForm}
          values={values}
          setFieldValue={setFieldValue}
          inputClassName={inputClassName}
          errors={errors}
          touched={touched}
        />
      );

    case 'heading': {
      const {
        canReset = false,
        canToggle = false,
        reset,
        resetToDefaults,
        resetTier,
        isAtDefault,
        toggle,
        tooltip,
        ...rest
      } = field;

      return (
        <div className="flex items-center gap-x-2 relative">
          <Heading {...rest} tooltip={tooltip} icon={DESIGN_SECTION_ICONS[rest.id]} />

          {(canReset || canToggle) && (
            <div className="flex items-center gap-1 absolute top-0 right-0 z-0">
              {canToggle && (
                <ToggleButton enabled={field.enabled} label={field.label} onClick={toggle} />
              )}

              {canReset && (
                <ResetButton
                  isAtDefault={isAtDefault}
                  resetTier={resetTier}
                  onClick={() => handleTieredReset({ resetTier, reset, resetToDefaults })}
                  className="ml-auto"
                />
              )}
            </div>
          )}
        </div>
      );
    }

    case 'font-combobox':
      return (
        <FontCombobox
          name={field.name}
          value={getNestedValue(values, field.name)}
          onChange={v => setFieldValue(field.name, v)}
          placeholder={field.placeholder || field.label}
          defaultLabel={field.defaultLabel}
        />
      );

    case 'font-weight-select':
      return (
        <FontWeightSelectInner
          name={field.name}
          value={getNestedValue(values, field.name)}
          fontFamily={getNestedValue(values, field.fontFamilyFieldName)}
          onChange={v => setFieldValue(field.name, v)}
          label={field.label}
          transformValue={field.transformValue}
          height={field.height}
          isDesignForm={field.isDesignForm}
          showAutoOption={field.showAutoOption}
          defaultValue={field.defaultValue}
        />
      );

    case 'label-toggle':
      return (
        <div className="border border-gray-200 dark:border-border rounded-lg bg-white dark:bg-background px-2.5 py-2">
          <LabelToggle {...field} />
        </div>
      );

    case 'image': {
      const FieldComponent = getFieldComponentByType(field.type);

      return (
        <div>
          {field.label && (
            <div className="text-xs font-medium text-gray-700 dark:text-muted-foreground mb-1.5">
              {field.label}
            </div>
          )}
          <FieldComponent
            {...field}
            values={values}
            setFieldValue={setFieldValue}
            inputClassName={inputClassName}
            errors={errors}
            touched={touched}
          />
        </div>
      );
    }

    default: {
      // Select fields don't get individual reset buttons — they reset via section/color resets.
      // Fall through to Form.js generic select rendering.
      if (field.type === 'select') return null;

      // Handle canReset wrapper for design text fields with reset buttons
      if (field.canReset) {
        const FieldComponent = getFieldComponentByType(field.type);
        const { hidePlaceHolder, canReset, resetValue, ...rest } = field;

        const fieldElement = React.createElement(FieldComponent, {
          ...rest,
          name: rest.name,
          id: rest.id || rest.name,
          placeholder: hidePlaceHolder ? '' : rest.placeholder || rest.label,
          defaultValue: rest.defaultValue,
          className: inputClassName,
        });

        const defaultValue = resetValue ?? '';
        const hasTier = field.resetTier !== undefined;
        const currentValue = getNestedValue(values, field.name);
        const atDefault = hasTier
          ? field.isAtDefault
          : currentValue != null && currentValue === defaultValue;

        return (
          <div className="flex items-center gap-1">
            <div className="flex-1">{fieldElement}</div>
            <ResetButton
              isAtDefault={atDefault}
              resetTier={field.resetTier}
              onClick={() => {
                if (hasTier && field.resetTier === 2) {
                  handleTieredReset({
                    resetTier: field.resetTier,
                    reset: field.reset || (() => setFieldValue(field.name, defaultValue)),
                  });
                } else if (field.reset) {
                  field.reset();
                } else {
                  setFieldValue(field.name, defaultValue);
                }
              }}
              className="shrink-0"
            />
          </div>
        );
      }

      // Not a design field — return null to fall back to Form.js generic rendering
      return null;
    }
  }
}
